import { CardBody, useRect } from '@statsbomb/kitbag-components';
import { PitchHeatmapLayer, PitchViz, Legend, GradientBar } from '@statsbomb/kitbag-datavis';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { showLegendAtom, showMarkerStartAtom } from '@/atoms/visualisation';
import { PITCH_HEIGHT, PITCH_WIDTH, SCALE_FACTOR } from '@/consts/HeatMap';
import { useGetPitchRotation } from '@/hooks/useGetPitchRotation';
import { useLoadableStatus } from '@/hooks/useLoadableStatus';
import { usePitchCrop } from '@/hooks/usePitchCrop';
import { useStatus } from '@/hooks/useStatus';
import { BaseEvent, VisEvent } from '@/types/event';
import { HeatMapProps } from '@/types/visualisation';
import { isNullish } from '@/utils/predicates';
import { CardHeightWrapper } from '../card/CardHeightWrapper';
import { ErrorCard } from '../contentState/ErrorCard';
import { EventMapSelectionLayer } from './EventMap/EventMapSelectionLayer';
import { PitchVisWithAttackingDirectionArrow } from './PitchVisualisation/PitchVisWithAttackingDirectionArrow';
import { PitchVisWrapper } from './PitchVisualisation/PitchVisWrapper';

const HeatMapBase = ({
  eventsForMetric = [],
  overlay,
  swapCropUnderAgainst,
}: {
  eventsForMetric?: VisEvent[] | BaseEvent[];
  overlay?: JSX.Element;
  swapCropUnderAgainst?: boolean;
}) => {
  const pitchRotation = useGetPitchRotation(false);
  const visibleArea = usePitchCrop(false, swapCropUnderAgainst);
  const showMarkerStart = useAtomValue(showMarkerStartAtom);
  const { t } = useTranslation('visualisation');
  const [legendRef, legendRect] = useRect<HTMLLegendElement>();
  const showLegend = useAtomValue(showLegendAtom);

  const pitchViz = (
    <PitchViz
      /* this is intentionally empty to overwrite pitch viz svg styles
    to position the arrow correctly */
      className=""
      rotationName={pitchRotation}
      pitchFocusZoneName={visibleArea}
      overlay={overlay}
      underlay={
        <PitchHeatmapLayer
          data={eventsForMetric.map(({ startX, startY, endX, endY }) => {
            const x = showMarkerStart || isNullish(endX) ? startX : endX;
            const y = showMarkerStart || isNullish(endY) ? startY : endY;

            return {
              x,
              y,
            };
          })}
          width={PITCH_WIDTH}
          height={PITCH_HEIGHT}
          scaleFactor={SCALE_FACTOR}
        />
      }
    />
  );

  return (
    <div className="h-full w-full" data-testid="heatmap-with-data">
      <PitchVisWrapper>
        {showLegend ? (
          <>
            <PitchVisWithAttackingDirectionArrow pitchVisType="heatmap" pitchRotation={pitchRotation}>
              {pitchViz}
            </PitchVisWithAttackingDirectionArrow>
            <Legend legendRef={legendRef}>
              <GradientBar
                colSpan={2}
                title={t('legend.heatMap.title')}
                minText={t('legend.heatMap.minText')}
                maxText={t('legend.heatMap.maxText')}
                legendWidth={legendRect.width}
              />
            </Legend>
          </>
        ) : (
          pitchViz
        )}
      </PitchVisWrapper>
    </div>
  );
};

const HeatMapWithData = ({
  pitchEventsAtom,
  unwrappedPitchEventsAtom,
  allowInteraction,
  swapCropUnderAgainst,
}: HeatMapProps) => {
  const { markerEvents, arrowEvents } = useAtomValue(unwrappedPitchEventsAtom);
  const allEvents = [...markerEvents, ...arrowEvents];

  const status = useLoadableStatus(pitchEventsAtom);
  const statusProps = useStatus('general', status);

  const selectionOverlay = allowInteraction ? (
    <EventMapSelectionLayer unwrappedPitchEventsAtom={unwrappedPitchEventsAtom} />
  ) : undefined;

  return (
    <CardHeightWrapper>
      <CardBody {...statusProps}>
        <HeatMapBase
          eventsForMetric={allEvents}
          overlay={selectionOverlay}
          swapCropUnderAgainst={swapCropUnderAgainst}
        />
      </CardBody>
    </CardHeightWrapper>
  );
};

export const HeatMap = (props: HeatMapProps) => (
  <ErrorCard translationNameSpace="general" fallbackChildren={<HeatMapBase eventsForMetric={[]} />}>
    <HeatMapWithData {...props} />
  </ErrorCard>
);
